import React from "react";
import { getEntityTaxDisplayLabel, getFormattedCurrency } from "../../../../../../shared/utils";
import { PurchaseOrderCardArgsType } from "./type";
import usePurchaseOrderCardService from "./service";
import { getInventoryTypeIcon } from "../../../../../../shared/utils/enumIcon";
import { useStaticData } from "../../../../../../context/staticData";
import Button from "../../../../../../shared/components/button";
import Variant from "../../../../../../shared/enums/Variant";
import ApiPurchaseOrderLine from "../../../../../../shared/model/ApiPurchaseOrderLine";
import PurchaseOrderAdjustments from "../purchaseOrderAdjustment";
import PaymentDetails from "../paymentDetail";
import { useCopies } from "../../../../../../shared/hooks/useCopies";
import { getDateTimeToDisplay } from "../../util";
import Icon from "assets/icons";
import PurchaseOrderStatus from "shared/enums/PurchaseOrderStatus";

export default function PurchaseOrderCard(props: PurchaseOrderCardArgsType) {
  const copies = useCopies();
  const { purchaseOrderStatus, transactionStatus } = useStaticData();
  const service = usePurchaseOrderCardService(props);
  return (
    <React.Fragment>
      <div className="purchaseHistory">
        <div className="purchaseHistory--wrapper">
          {props.purchaseOrder.purchaseOrderStatus === PurchaseOrderStatus.COMPLETED && (
            <div className="purchaseHistory--invoiceBtnWrapper">
              <div className="purchaseHistory--invoiceBtnGroup">
                <Button
                  variant={Variant.LINK}
                  icon={<Icon.Send />}
                  label={copies.get("purchase.history.send.invoice")}
                  onClick={service.sendInvoiceHandler}
                />
                {service.isTemplateEnabledForDownload && (
                  <React.Fragment>
                    <span className="bulletDot"></span>
                    <Button
                      variant={Variant.LINK}
                      icon={<Icon.DownloadImage />}
                      label={copies.get("purchase.order.download.invoice")}
                      onClick={service.downloadInvoiceHandler}
                    />
                  </React.Fragment>
                )}
              </div>
            </div>
          )}
          <div className="purchaseHistory--card">
            <div className="purchaseHistory--card-stickedContent">
              <p className="purchaseHistory--card-stickedContent_orderId">
                {copies.get("purchase.history.order.id", { 0: service.purchaseOrderId })}
              </p>
              {props.purchaseOrder.purchaseOrderStatus !== PurchaseOrderStatus.CREATED && (
                <p className="purchaseHistory--card-stickedContent_status">
                  {purchaseOrderStatus[props.purchaseOrder.purchaseOrderStatus]}
                </p>
              )}
            </div>
            <div className="purchaseHistory--card-mainContent">
              <div className="purchaseHistory--card-lineItemsDetails">
                {props.purchaseOrder.lines.map(lineItem => (
                  <PurchaseOrderLineItem
                    key={lineItem.purchaseOrderLineId}
                    lineItem={lineItem}
                    reason={props.purchaseOrder.reason}
                  />
                ))}
              </div>
              <div className="purchaseHistory--card-dateTimePaymentDetails">
                <div className="purchaseHistory--card-dateTimePaymentDetails_dateTime">
                  <label htmlFor="">{copies.get("date.time")}</label>
                  <p>{getDateTimeToDisplay(props.purchaseOrder.createdDate)}</p>
                </div>
                {props.purchaseOrder.transaction?.transactionMethodType && (
                  <div className="purchaseHistory--card-dateTimePaymentDetails_paymentMethod">
                    <label htmlFor="">{copies.get("payment.method")}</label>
                    <PaymentDetails transaction={props.purchaseOrder.transaction} />
                  </div>
                )}
                <div className="purchaseHistory--card-dateTimePaymentDetails_transactBy">
                  <label htmlFor="">{copies.get("transaction.by")}</label>
                  <p>{props.purchaseOrder.transactionBy}</p>
                </div>
                {!service.isInvoicePaymentPending && props.purchaseOrder.invoicePaymentRequest?.invoicePayerEmail && (
                  <div className="purchaseHistory--card-dateTimePaymentDetails_transactBy">
                    <label htmlFor="">{copies.get("invoice.paid.by.label")}</label>
                    <p>{props.purchaseOrder.invoicePaymentRequest?.invoicePayerEmail}</p>
                  </div>
                )}
                {props.purchaseOrder.transaction?.status && (
                  <div className="purchaseHistory--card-dateTimePaymentDetails_paymentMethod">
                    <label htmlFor="">{copies.get("transaction.status")}</label>
                    <p>{transactionStatus[props.purchaseOrder.transaction.status]}</p>
                  </div>
                )}
                <div className="purchaseHistory--card-dateTimePaymentDetails_separator" />
              </div>
              <div className="purchaseHistory--card-amountDetails">
                <label htmlFor="" className="purchaseHistory--card-amountDetails_total">
                  {copies.get("total.amount")}
                </label>
                <h4 className="purchaseHistory--card-amountDetails_amount">
                  {getFormattedCurrency(props.purchaseOrder.amount)}
                </h4>
                {service.isInvoicePaymentPending && props.purchaseOrder.invoicePaymentRequest?.invoicePayerEmail && (
                  <div className="purchaseHistory--card-amountDetails_invPayerInfo">
                    <Icon.InfoCircle className="purchaseHistory--card-amountDetails_invPayerInfo--icon" />
                    <span className="purchaseHistory--card-amountDetails_invPayerInfo--label">
                      {copies.get("invoice.payment.was.requested.to")}
                    </span>
                    <span className="purchaseHistory--card-amountDetails_invPayerInfo--text">
                      {props.purchaseOrder.invoicePaymentRequest?.invoicePayerEmail}
                    </span>
                  </div>
                )}
                {props.makePayment}
                <PurchaseOrderAdjustments
                  purchaseOrderAdjustments={props.purchaseOrder.purchaseOrderAdjustments}
                  purchaseOrder={props.purchaseOrder}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </React.Fragment>
  );
}

function PurchaseOrderLineItem(props: { lineItem: ApiPurchaseOrderLine; reason?: string }) {
  const copies = useCopies();
  const { inventoryType } = useStaticData();
  const icon = getInventoryTypeIcon(props.lineItem.lineType);
  return (
    <div className="purchaseHistorylineItems--wrapper">
      <div className="purchaseHistorylineItems--item">
        <div className="purchaseHistorylineItems--icon">
          <span>{icon}</span>
        </div>
        <div className="purchaseHistorylineItems--details">
          <h5>
            {inventoryType[props.lineItem.lineType]}
            {props.lineItem.lineTypeDescription && <span>{` - ${props.lineItem.lineTypeDescription}`}</span>}
          </h5>
          {props.lineItem.couponCode && (
            <p>
              {copies.get("coupon.applied")} : <span className="couponCode--tag">{props.lineItem.couponCode}</span>
            </p>
          )}
          <table className="purchaseHistorylineItems--amountBreakups">
            <thead>
              <tr>
                <th>{copies.get("quantity.short")}</th>
                <th>{copies.get("price")}</th>
                <th>{copies.get("tax")}</th>
                <th>{copies.get("total")}</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>{props.lineItem.quantity}</td>
                <td>{getFormattedCurrency(props.lineItem.lineAmount)}</td>
                <td>
                  <p className="purchaseHistorylineItems_taxDetails">
                    {getFormattedCurrency(props.lineItem.taxAmount)}{" "}
                    <small>
                      {getEntityTaxDisplayLabel(copies, "line.item.tax.info", props.lineItem.taxConfiguration)}
                    </small>
                  </p>
                </td>
                <td>{getFormattedCurrency(props.lineItem.totalAmount)}</td>
              </tr>
            </tbody>
          </table>
          {props.reason && (
            <div className="purchaseHistorylineItems--note">
              <span>
                <Icon.Note /> {copies.get("note.from.admin")}:
              </span>{" "}
              {props.reason}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
